import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Mail, Instagram, MessageCircle, Cookie } from 'lucide-react';

// Logo SVG - Same as Navbar
const LogoSVG = ({ className = "w-10 h-10" }) => (
  <svg viewBox="0 0 100 100" className={className} fill="none">
    <defs>
      <linearGradient id="goldLogoFooter" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#e8d5a3"/>
        <stop offset="50%" stopColor="#c9a227"/>
        <stop offset="100%" stopColor="#b8922a"/>
      </linearGradient>
      <radialGradient id="glowLogoFooter" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#c9a227" stopOpacity="0.3"/>
        <stop offset="100%" stopColor="#c9a227" stopOpacity="0"/>
      </radialGradient>
      <radialGradient id="lightPointFooter" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#ffffff" stopOpacity="1"/>
        <stop offset="40%" stopColor="#e8d5a3" stopOpacity="0.8"/>
        <stop offset="100%" stopColor="#c9a227" stopOpacity="0"/>
      </radialGradient>
    </defs>
    <circle cx="50" cy="50" r="38" fill="url(#glowLogoFooter)"/>
    <circle cx="50" cy="50" r="28" fill="url(#goldLogoFooter)"/>
    <path d="M38 65 L50 30 L62 65" stroke="#f8f4e8" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <circle cx="50" cy="30" r="8" fill="url(#lightPointFooter)"/>
    <circle cx="50" cy="30" r="4" fill="#ffffff"/>
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const handleReopenCookies = () => {
    try {
      localStorage.removeItem('akashica_tracking_consent');
      window.location.reload();
    } catch (e) { /* noop */ }
  };

  return (
    <footer className="bg-[hsl(var(--sage-light))] border-t border-[hsl(var(--border))]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-10 md:gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <LogoSVG className="w-10 h-10" />
              <span className="font-heading text-xl text-[hsl(var(--text-primary))]">
                Escuela Akáshica
              </span>
            </Link>
            <p className="text-[hsl(var(--text-secondary))] leading-relaxed max-w-md font-body">
              Un espacio para aprender a sostener tu energía, tu consciencia y tu vida. 
              Formación en consciencia encarnada, claridad interna y autonomía personal.
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-heading text-lg text-[hsl(var(--text-primary))] mb-4">
              Explora
            </h4>
            <ul className="space-y-3">
              <li>
                <Link 
                  to="/formaciones" 
                  className="text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--accent))] transition-colors"
                  data-testid="footer-formaciones"
                >
                  Formaciones
                </Link>
              </li>
              <li>
                <Link 
                  to="/quien-soy" 
                  className="text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--accent))] transition-colors"
                  data-testid="footer-quien-soy"
                >
                  Quién soy
                </Link>
              </li>
              <li>
                <Link 
                  to="/metodologia" 
                  className="text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--accent))] transition-colors"
                  data-testid="footer-metodologia"
                >
                  Metodología
                </Link>
              </li>
              <li>
                <Link 
                  to="/faq" 
                  className="text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--accent))] transition-colors"
                  data-testid="footer-faq"
                >
                  Preguntas frecuentes
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading text-lg text-[hsl(var(--text-primary))] mb-4">
              Contacto
            </h4>
            <ul className="space-y-3">
              <li>
                <Link 
                  to="/contacto" 
                  className="flex items-center gap-2 text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--accent))] transition-colors"
                  data-testid="footer-contacto"
                >
                  <Mail className="w-4 h-4" strokeWidth={1.5} />
                  Escríbeme
                </Link>
              </li>
              <li>
                <a 
                  href="https://instagram.com/escuela_consciencia_akashica" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--accent))] transition-colors"
                  data-testid="footer-instagram"
                  aria-label="Instagram de la escuela"
                  title="@escuela_consciencia_akashica"
                >
                  <Instagram className="w-4 h-4" strokeWidth={1.5} />
                  Instagram
                </a>
              </li>
              <li>
                <a 
                  href="https://wa.me/34654827313" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-[hsl(var(--text-secondary))] hover:text-green-600 transition-colors"
                  data-testid="footer-whatsapp"
                >
                  <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
                  WhatsApp
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-heading text-lg text-[hsl(var(--text-primary))] mb-4">
              Legal
            </h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/aviso-legal"
                  className="text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--accent))] transition-colors text-sm"
                  data-testid="footer-aviso-legal"
                >
                  Aviso legal
                </Link>
              </li>
              <li>
                <Link
                  to="/privacidad"
                  className="text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--accent))] transition-colors text-sm"
                  data-testid="footer-privacidad"
                >
                  Política de privacidad
                </Link>
              </li>
              <li>
                <Link
                  to="/cookies"
                  className="text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--accent))] transition-colors text-sm"
                  data-testid="footer-cookies"
                >
                  Política de cookies
                </Link>
              </li>
              <li>
                <Link
                  to="/terminos"
                  className="text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--accent))] transition-colors text-sm"
                  data-testid="footer-terminos"
                >
                  Términos y condiciones
                </Link>
              </li>
              <li>
                <Link
                  to="/desistimiento"
                  className="text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--accent))] transition-colors text-sm"
                  data-testid="footer-desistimiento"
                >
                  Desistimiento (PDF)
                </Link>
              </li>
              <li>
                <button
                  onClick={handleReopenCookies}
                  className="flex items-center gap-2 text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--accent))] transition-colors text-sm"
                  data-testid="footer-reopen-cookies"
                >
                  <Cookie className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Configurar cookies
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Titular info - LSSI art. 10 */}
        <div className="border-t border-[hsl(var(--border))] mt-12 pt-6 text-xs text-[hsl(var(--text-tertiary))] leading-relaxed">
          <p>
            Titular: <strong>Herminio Vázquez Vigón</strong> · Autónomo persona física · Domicilio: Barcelona (España) · Contacto: <a href="mailto:hola@herminiovazquez.com" className="hover:text-[hsl(var(--accent))] transition-colors">hola@herminiovazquez.com</a>
          </p>
        </div>

        {/* Bottom */}
        <div className="border-t border-[hsl(var(--border))] mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-[hsl(var(--text-tertiary))]">
            © {currentYear} Escuela de Consciencia Akáshica. Todos los derechos reservados.
          </p>
          <p className="text-sm text-[hsl(var(--text-tertiary))] flex items-center gap-1">
            Hecho con <Heart className="w-4 h-4 text-[hsl(var(--accent))]" fill="currentColor" /> por Herminio
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
